import { HyperBackendMethod } from "@shared/backend/hyper-backend-method";
import { Controller, remult } from "remult";
import { Mission } from "./";

@Controller("mission-methods")
export class MissionMethods {
  /**
   * Updates the order of a mission when dragged to a new position within the same status column
   * @param missionId The ID of the mission being moved
   * @param newOrder The new order position
   * @param organizationId The organization ID for filtering
   */
  @HyperBackendMethod({ allowed: true, overrideSecurity: true })
  static async updateMissionOrder(missionId: string, newOrder: number) {
    const repo = remult.repo(Mission);
    const mission = await repo.findId(missionId);

    if (!mission) {
      throw new Error("Mission not found");
    }

    const currentStatus = mission.status;
    const oldOrder = mission.order;

    // Get all missions with the same status in the same organization
    const missionsInSameStatus = await repo.find({
      where: {
        status: currentStatus,
        isArchived: false,
        date: currentStatus === "DONE" ? { $gte: new Date() } : {},
      },
      orderBy: { order: "asc" },
    });

    // Update orders
    if (oldOrder < newOrder) {
      // Moving down: decrease order for missions between old and new position
      for (const m of missionsInSameStatus) {
        if (m.order > oldOrder && m.order <= newOrder && m.id !== missionId) {
          m.order--;
          await repo.save(m);
        }
      }
    } else if (oldOrder > newOrder) {
      // Moving up: increase order for missions between new and old position
      for (const m of missionsInSameStatus) {
        if (m.order >= newOrder && m.order < oldOrder && m.id !== missionId) {
          m.order++;
          await repo.save(m);
        }
      }
    }

    // Update the moved mission's order
    mission.order = newOrder;
    await repo.save(mission);

    return true;
  }

  /**
   * Updates both status and order when a mission is moved to a different status column
   * @param missionId The ID of the mission being moved
   * @param newStatus The new status for the mission
   * @param newOrder The new order position
   * @param organizationId The organization ID for filtering
   */
  @HyperBackendMethod({ allowed: true, overrideSecurity: true })
  static async moveMissionToNewStatus(
    missionId: string,
    newStatus: Mission["status"],
    newOrder: number,
    organizationId: string,
  ) {
    const missionRepo = remult.repo(Mission);
    const mission = await missionRepo.findId(missionId);

    if (!mission) {
      throw new Error("Mission not found");
    }

    const oldStatus = mission.status;

    // Get missions in old status column and update their orders
    const missionsInOldStatus = await missionRepo.find({
      where: {
        status: oldStatus,
        isArchived: false,
        organizationId,
      },
      orderBy: { order: "asc" },
    });

    // Remove the mission from old status and compress the order sequence
    for (const m of missionsInOldStatus) {
      if (m.order > mission.order && m.id !== missionId) {
        m.order--;
        await missionRepo.save(m);
      }
    }

    // Get missions in new status column
    const missionsInNewStatus = await missionRepo.find({
      where: {
        status: newStatus,
        organizationId: mission.organizationId,
        isArchived: false,
      },
      orderBy: { order: "asc" },
    });
    console.log("missionsInNewStatus: ", missionsInNewStatus);

    // Make space for the newly added mission
    for (const m of missionsInNewStatus) {
      if (m.order >= newOrder && m.id !== missionId) {
        m.order++;
        console.log("m: ", m);
        await missionRepo.save(m);
      }
    }

    // Update the moved mission's status and order
    mission.status = newStatus;
    console.log("newStatus: ", newStatus);
    mission.order = newOrder;
    console.log("newOrder: ", newOrder);
    await missionRepo.save(mission);

    return true;
  }

  /**
   * Gets the next available order for a new mission in a specific status column
   * @param status The status column
   * @param organizationId The organization ID for filtering
   */
  @HyperBackendMethod({ allowed: true, overrideSecurity: true })
  static async getNextOrderForStatus(
    status: Mission["status"],
    organizationId: string,
  ): Promise<number> {
    const repo = remult.repo(Mission);

    // Get the highest order in the specified status column
    const missionsInStatus = await repo.find({
      where: {
        status,
        organizationId,
        isArchived: false,
      },
      orderBy: { order: "desc" },
      limit: 1,
    });

    return missionsInStatus.length ? missionsInStatus[0].order + 1 : 0;
  }
}
